import { useState } from "react"
import { EditableCombobox, type ComboboxOption } from "./editable-combobox"
import { Button } from "./button"
import { User, Title } from "@promo/shared"
import { put } from "@/lib/api"
import { cn } from "@/lib/utils"
import { useQueryClient } from "@tanstack/react-query"
import { useTitles } from "@/hooks/useTitles"
import { AddTitleDialog } from "@/components/dialogs/AddTitleDialog"

interface EditableTitleSelectProps {
  userId: string;
  currentTitle?: string;
  onTitleUpdate?: (newTitle: string) => void;
  className?: string;
  selectClassName?: string;
  disabled?: boolean;
}

export function EditableTitleSelect({
  userId,
  currentTitle,
  onTitleUpdate,
  className,
  selectClassName,
  disabled,
}: EditableTitleSelectProps) {
  const [isUpdating, setIsUpdating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const queryClient = useQueryClient();
  const { data: titles, isLoading: isTitlesLoading } = useTitles();

  // Convert titles to combobox options
  const titleOptions: ComboboxOption[] = titles?.map(title => ({
    value: title.name,
    label: title.name
  })) || [];

  const handleTitleChange = async (newTitle: string) => {
    if (newTitle === currentTitle) return;

    setIsUpdating(true);
    setError(null);

    try {
      const updatedUser = await put<User>(`/users/${userId}`, { title: newTitle });
      
      // Invalidate the user cache to reflect the updated title
      await queryClient.invalidateQueries({ queryKey: ['user', userId] });
      
      onTitleUpdate?.(updatedUser.title || newTitle);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update title');
      console.error('Failed to update user title:', err);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleNewTitleCreated = (newTitle: Title) => {
    // Auto-select and save the newly created title
    handleTitleChange(newTitle.name);
    setShowAddDialog(false);
  };

  // Use currentTitle directly - let the combobox handle empty values properly
  const displayValue = currentTitle || "";
  
  // Different placeholder text based on whether component is disabled (readonly) or interactive
  const placeholder = disabled 
    ? "No title set"      // Readonly mode shows this
    : "Search or select title";  // Interactive mode shows this

  // Create the footer content with the "Add new title" link
  const footerContent = !disabled && (
    <Button 
      variant="link" 
      size="sm"
      onClick={() => setShowAddDialog(true)}
      className="w-full justify-start p-0 h-auto text-xs text-muted-foreground hover:text-primary"
      disabled={isUpdating || isTitlesLoading}
    >
      + Add new title
    </Button>
  );

  return (
    <div className={cn("relative", className)}>
      <EditableCombobox
        value={displayValue}
        onValueChange={handleTitleChange}
        options={titleOptions}
        placeholder={placeholder}
        disabled={disabled || isUpdating || isTitlesLoading}
        footer={footerContent}
        className={cn(
          "text-sm text-muted-foreground",
          !currentTitle && "text-gray-400 italic",
          isUpdating && "opacity-50 cursor-not-allowed",
          error && "text-red-500",
          selectClassName
        )}
      />

      {/* Error Message */}
      {error && (
        <div className="absolute top-full left-0 mt-1 text-xs text-red-500 z-10">
          {error}
        </div>
      )}

      {/* Add Title Dialog */}
      <AddTitleDialog
        open={showAddDialog}
        onClose={() => setShowAddDialog(false)}
        onTitleCreated={handleNewTitleCreated}
      />
    </div>
  );
} 